import React, { useState, useEffect } from "react";
import {
  User,
  Lock,
  Calculator,
  LogOut,
  Eye,
  EyeOff,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DashboardContable = () => {
  // Estados principales
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [financialData, setFinancialData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const API_URL = "https://estudiolacasa.com.py/api";

  // Función para hacer login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");

    try {
      const response = await fetch(`${API_URL}/login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCurrentUser(data.data);
        setIsLoggedIn(true);
        loadFinancialData(data.data.id);
      } else {
        setLoginError(data.message || "Error al iniciar sesión");
      }
    } catch (error) {
      setLoginError("Error de conexión con el servidor");
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos financieros
  const loadFinancialData = async (usuarioId, ano = null) => {
    const yearToUse = ano || selectedYear;
    setLoadingData(true);

    try {
      const url = `${API_URL}/get_datos.php?usuario_id=${usuarioId}&ano=${yearToUse}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        setFinancialData(data.data);
      } else {
        setFinancialData([]);
      }
    } catch (error) {
      setFinancialData([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Función para cerrar sesión
  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setFinancialData([]);
    setLoginForm({ username: "", password: "" });
    setSelectedYear(new Date().getFullYear()); // Resetear al año actual
  };

  // Calcular métricas
  const metrics = React.useMemo(() => {
    if (!Array.isArray(financialData) || financialData.length === 0) {
      return {
        totalIngresos: 0,
        totalEgresos: 0,
        utilidad: 0,
        margenUtilidad: 0,
      };
    }

    const totalIngresos = financialData.reduce(
      (sum, item) => sum + (parseInt(item.ingresos) || 0),
      0
    );

    const totalEgresos = financialData.reduce(
      (sum, item) => sum + (parseInt(item.egresos) || 0),
      0
    );

    const utilidad = totalIngresos - totalEgresos;
    const margenUtilidad =
      totalIngresos > 0 ? (utilidad / totalIngresos) * 100 : 0;

    return { totalIngresos, totalEgresos, utilidad, margenUtilidad };
  }, [financialData]);

  // Pantalla de Login
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-blue-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Calculator className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Dashboard Contable
            </h1>
            <p className="text-gray-600">
              Ingresa tus credenciales para continuar
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, username: e.target.value })
                  }
                  className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingresa tu usuario"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, password: e.target.value })
                  }
                  className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingresa tu contraseña"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin h-5 w-5 mr-2" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </button>
          </form>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Usuarios de prueba:</p>
            <div className="text-xs text-gray-500 space-y-1">
              <div>admin / admin123</div>
              <div>usuario1 / pass123</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard Principal
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-lg p-2">
                <Calculator className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Dashboard Contable
                </h1>
                <p className="text-sm text-gray-600">
                  {currentUser?.empresa || "Contabilidad S.A."}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Selector de Año */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Seleccionar año:</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loadingData}
                >
                  {/* Generar años desde 2020 hasta el año actual + 1 */}
                  {Array.from(
                    { length: new Date().getFullYear() - 2019 },
                    (_, i) => {
                      const year = 2020 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    }
                  )}
                </select>
              </div>

              <button
                onClick={() => loadFinancialData(currentUser.id)}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                disabled={loadingData}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loadingData ? "animate-spin" : ""}`}
                />
                <span>Actualizar</span>
              </button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {currentUser?.nombre}
                </p>
                <p className="text-xs text-gray-500">Bienvenido</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido del Dashboard */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loadingData ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
            <span className="ml-3 text-gray-600">Cargando datos...</span>
          </div>
        ) : financialData.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">
              No hay datos disponibles
            </h3>
            <p className="text-yellow-600 mb-4">
              No se encontraron datos financieros para mostrar.
            </p>
            <button
              onClick={() => loadFinancialData(currentUser.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Cargar Datos
            </button>
          </div>
        ) : (
          <>
            {/* Indicador de Año Seleccionado */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <span className="text-blue-600 font-bold text-lg">
                      {selectedYear}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Reporte Financiero {selectedYear}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Mostrando datos para el año seleccionado
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>{financialData.length} registros encontrados</p>
                </div>
              </div>
            </div>

            {/* Tarjetas de Métricas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Ingresos */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Ingresos
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₲{metrics.totalIngresos.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {financialData.length} meses
                    </p>
                  </div>
                  <div className="bg-green-100 rounded-full p-3">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Total Egresos */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Egresos
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₲{metrics.totalEgresos.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Gastos del año</p>
                  </div>
                  <div className="bg-red-100 rounded-full p-3">
                    <TrendingDown className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </div>

              {/* Utilidad */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Utilidad Neta
                    </p>
                    <p
                      className={`text-2xl font-bold ${
                        metrics.utilidad >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      ₲{metrics.utilidad.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {metrics.utilidad >= 0 ? "Ganancia" : "Pérdida"}
                    </p>
                  </div>
                  <div
                    className={`${
                      metrics.utilidad >= 0 ? "bg-green-100" : "bg-red-100"
                    } rounded-full p-3`}
                  >
                    <DollarSign
                      className={`h-6 w-6 ${
                        metrics.utilidad >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Margen */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Margen</p>
                    <p
                      className={`text-2xl font-bold ${
                        metrics.margenUtilidad >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {metrics.margenUtilidad.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Rentabilidad</p>
                  </div>
                  <div className="bg-blue-100 rounded-full p-3">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Gráficos: Líneas y Barras */}
            <div className="bg-white rounded-xl p-6 shadow-sm border mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Evolución Mensual {selectedYear}
                </h3>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>Año:</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                    {selectedYear}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Gráfico de Líneas */}
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Evolución Mensual - Ingresos vs Egresos
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={financialData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" />
                      <YAxis
                        tickFormatter={(value) =>
                          `₲${(value / 1000000).toFixed(0)}M`
                        }
                      />
                      <Tooltip
                        formatter={(value) => [
                          `₲${parseInt(value).toLocaleString()}`,
                          "",
                        ]}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="ingresos"
                        stroke="#0920F7"
                        strokeWidth={3}
                        name="Ingresos"
                        dot={{ fill: "#0920F7", strokeWidth: 2, r: 5 }}
                        activeDot={{
                          r: 7,
                          stroke: "#0920F7",
                          strokeWidth: 2,
                          fill: "#ffffff",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="egresos"
                        stroke="#f97316"
                        strokeWidth={3}
                        name="Egresos"
                        dot={{ fill: "#f97316", strokeWidth: 2, r: 5 }}
                        activeDot={{
                          r: 7,
                          stroke: "#f97316",
                          strokeWidth: 2,
                          fill: "#ffffff",
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Gráfico de Barras */}
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Comparativo Mensual
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={financialData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" />
                      <YAxis
                        tickFormatter={(value) =>
                          `₲${(value / 1000000).toFixed(0)}M`
                        }
                      />
                      <Tooltip
                        formatter={(value) => [
                          `₲${parseInt(value).toLocaleString()}`,
                          "",
                        ]}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="ingresos" fill="#0920F7" name="Ingresos" />
                      <Bar dataKey="egresos" fill="#f97316" name="Egresos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Leyenda */}
              <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: "#0920F7" }}
                  ></div>
                  <span className="text-gray-600">Ingresos</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-gray-600">Egresos</span>
                </div>
              </div>
            </div>

            {/* Tabla Detallada */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Detalle Mensual - Año {selectedYear}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ingresos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Egresos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Resultado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Margen %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {financialData.map((row, index) => {
                      const ingresos = parseInt(row.ingresos) || 0;
                      const egresos = parseInt(row.egresos) || 0;
                      const resultado = ingresos - egresos;
                      const margen =
                        ingresos > 0 ? (resultado / ingresos) * 100 : 0;

                      return (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.mes} {row.año}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            ₲{ingresos.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            ₲{egresos.toLocaleString()}
                          </td>
                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                              resultado >= 0 ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            ₲{resultado.toLocaleString()}
                          </td>
                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                              margen >= 0 ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {margen.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default DashboardContable;
